import {
  AlertTriangle,
  ArrowLeftRight,
  BookOpen,
  Calculator,
  CalendarDays,
  ChevronDown,
  Download,
  FileCode,
  LineChart as LineChartIcon,
  Loader2,
  Wallet,
} from 'lucide-react';
import {
  REVENUE_OFFICIAL_VERSION,
  RevenueLedgerVersionSchema,
  type RevenueLedgerVersion,
} from '@cortex/domain-ac-mens-hub-api';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { ChartFilterBar } from '../components/chart/ChartFilterBar.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';
import { RevenueCompareView } from '../components/revenue/ledger/RevenueCompareView.js';
import {
  RevenueLedgerPanel,
  type RevenueViewMode,
} from '../components/revenue/ledger/RevenueLedgerPanel.js';
import { RevenueVersionApproval } from '../components/revenue/ledger/RevenueVersionApproval.js';
import { RevenueChartView } from '../components/revenue/RevenueChartView.js';
import { RevenueDefinitionView } from '../components/revenue/RevenueDefinitionView.js';
import { RevenueDeferredView } from '../components/revenue/RevenueDeferredView.js';
import { RevenueMonthlyView } from '../components/revenue/RevenueMonthlyView.js';
import { RevenueValidationView } from '../components/revenue/RevenueValidationView.js';
import { useRevenueRecords, useValidationLogs } from '../hooks/useRevenue.js';
import { useLedgerMonthly } from '../hooks/useRevenueLedger.js';
import { revenueApi } from '../lib/api.js';
import { downloadCsv, downloadFile, downloadSql } from '../lib/csv.js';
import { aggregateMonthly, formatSnapshotJST } from '../lib/revenue-aggregation.js';
import { jstDate, monthRange, REVENUE_VERSION_OPTIONS } from '../lib/revenue-ledger-view.js';
import {
  buildAggregationLogicText,
  buildIndividualSqlText,
  buildLedgerCsv,
  buildMonthlyRevenueCsv,
  buildRawDataCsv,
  buildRevenueCsv,
  buildSqlExportText,
  RAW_DATA_QUERIES,
  REVENUE_START_DATE,
} from '../lib/revenue-export.js';
import type { RawDataTableKey, RevenueRecord } from '../types/revenue.js';

/** チャート/月次/前受金・貸倒/版の比較/要対応/定義の6タブ。 @graph-connects none */
type ViewMode = RevenueViewMode;

/** labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const TABS: { key: ViewMode; labelKey: string; icon: typeof LineChartIcon }[] = [
  { key: 'chart', labelKey: 'revenue.tabChart', icon: LineChartIcon },
  { key: 'monthly', labelKey: 'revenue.tabMonthly', icon: CalendarDays },
  { key: 'deferred', labelKey: 'revenue.tabDeferred', icon: Wallet },
  { key: 'compare', labelKey: 'revenue.tabCompare', icon: ArrowLeftRight },
  { key: 'validation', labelKey: 'revenue.tabValidation', icon: Calculator },
  { key: 'definition', labelKey: 'revenue.tabDefinition', icon: BookOpen },
];

/** 退会失効の出し入れの選択肢。labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const WITHDRAWAL_OPTIONS: { key: 'include' | 'exclude'; labelKey: string }[] = [
  { key: 'include', labelKey: 'revenue.withdrawalInclude' },
  { key: 'exclude', labelKey: 'revenue.withdrawalExclude' },
];

/** labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const RAW_DATA_TABLE_LABELS: { key: RawDataTableKey; labelKey: string }[] = [
  { key: 'payments', labelKey: 'revenue.rawPayments' },
  { key: 'tickets', labelKey: 'revenue.rawTickets' },
  { key: 'cancellations', labelKey: 'revenue.rawCancellations' },
  { key: 'orders', labelKey: 'revenue.rawOrders' },
  { key: 'deliveries', labelKey: 'revenue.rawDeliveries' },
  { key: 'payment_status', labelKey: 'revenue.rawPaymentStatus' },
  { key: 'refunds', labelKey: 'revenue.rawRefunds' },
];

/** @graph-connects none */
function ViewModeTabs({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap rounded-lg border border-[var(--color-border)]">
      {TABS.map(({ key, labelKey, icon: Icon }, i) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            onChange(key);
          }}
          className={`flex items-center gap-1 border-[var(--color-border)] px-3 py-2 text-xs transition-colors ${
            mode === key
              ? 'bg-[var(--color-accent-primary)] text-white'
              : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
          } ${i === 0 ? 'rounded-l-lg' : 'border-l'} ${i === TABS.length - 1 ? 'rounded-r-lg' : ''}`}
          aria-label={t('revenue.tabAriaLabel', { name: t(labelKey) })}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {t(labelKey)}
        </button>
      ))}
    </div>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business ドロップダウンメニューコンポーネント。CSV/SQLエクスポートの選択肢を表示する。
 * @graph-connects none
 */
function DropdownMenu({
  trigger,
  items,
}: {
  trigger: { label: string; icon: typeof Download; disabled?: boolean };
  items: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    separator?: boolean;
    disabled?: boolean;
  }[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    /** @graph-connects none */
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    /** @graph-connects none */
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const Icon = trigger.icon;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
        }}
        disabled={trigger.disabled}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1.5 text-xs text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {trigger.label}
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 z-50 mt-1 min-w-[240px] rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated,#fff)] py-1 shadow-xl"
          style={{ backdropFilter: 'none' }}
        >
          {items.map((item, i) => (
            <div key={item.label}>
              {item.separator && i > 0 && (
                <div className="mx-2 my-1 border-t border-[var(--color-border)]" />
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  item.onClick();
                  setOpen(false);
                }}
                disabled={item.loading === true || item.disabled === true}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-bg-subtle)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {item.loading && <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business ac-mens Hub 売上集計ページ。定義の版（v0.1 正式 / v0 旧Hub）を切り替え、版の選択欄の横に選んでいる版の承認者と決定日を出し、チャート/月次/前受金・貸倒/版の比較/要対応/定義の6タブで表示する。v0.1 は定義の版つき台帳を読み、月の締め（権限 revenue-close）と退会失効の出し入れができる。v0 は旧Hubの画面をそのまま出し、会計定義と異なる旨を常に表示する。CSV（日次/月次/台帳/rawデータ）ダウンロードとSQL出力に対応。
 * @graph-connects useRevenueRecords [calls] revenue__daily 取得フック
 * @graph-connects useValidationLogs [calls] revenue__validation_log 取得フック
 * @graph-connects useLedgerMonthly [calls] 版つき台帳の月次（台帳の CSV と最終更新）
 * @graph-connects RevenueVersionApproval [calls] 版の選択欄の横の承認者・決定日
 * @graph-connects RevenueLedgerPanel [calls] 正式版の各タブ
 * @graph-connects RevenueCompareView [calls] 旧Hubを選んだときの版の比較タブ
 * @graph-connects revenueApi [calls] rawデータ API
 * @graph-connects RevenueChartView [calls] チャートタブ
 * @graph-connects RevenueMonthlyView [calls] 月次タブ
 * @graph-connects RevenueDeferredView [calls] 旧Hubの前受金・貸倒タブ
 * @graph-connects RevenueDefinitionView [calls] 旧Hubの定義タブ
 * @graph-connects RevenueValidationView [calls] 旧Hubの要対応タブ
 */
export function RevenuePage() {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('chart');
  const [loadingRawData, setLoadingRawData] = useState<RawDataTableKey | null>(null);
  const [from, setFrom] = useState(REVENUE_START_DATE);
  const [to, setTo] = useState('');
  const [version, setVersion] = useState<RevenueLedgerVersion>(REVENUE_OFFICIAL_VERSION);
  const [includeWithdrawal, setIncludeWithdrawal] = useState(true);
  const isOfficial = version === 'v0.1';
  // 版の選択欄の説明として承認の表示を読み上げさせる（aria-describedby）
  const approvalId = useId();
  // 旧Hub（v0）の日次表と検算履歴は、v0 を見ているときだけ読む
  const recordsQuery = useRevenueRecords(from, to || undefined, !isOfficial);
  const validationQuery = useValidationLogs(!isOfficial && viewMode === 'validation');

  const toDate = to || jstDate(-1);
  const fromMonth = from.slice(0, 7);
  const toMonth = (to || jstDate(0)).slice(0, 7);
  // 台帳の CSV と最終更新に使う。タブの中身は RevenueLedgerPanel が同じキーで読む（取得は1回にまとまる）
  const ledger = useLedgerMonthly(
    { version: 'v0.1', fromMonth, toMonth },
    isOfficial && fromMonth <= toMonth,
  ).data;

  const columns = recordsQuery.data?.columns ?? [];
  const rows = useMemo<RevenueRecord[]>(
    () => [...(recordsQuery.data?.rows ?? [])].sort((a, b) => (a.date < b.date ? -1 : 1)),
    [recordsQuery.data],
  );

  const handleDailyCsvDownload = useCallback(() => {
    if (columns.length === 0 || rows.length === 0) return;
    const csv = buildRevenueCsv(columns, rows);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`revenue_daily_${today}.csv`, csv);
  }, [columns, rows]);

  const handleMonthlyCsvDownload = useCallback(() => {
    if (columns.length === 0 || rows.length === 0) return;
    const buckets = aggregateMonthly(rows);
    const csv = buildMonthlyRevenueCsv(columns, buckets);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`revenue_monthly_${today}.csv`, csv);
  }, [columns, rows]);

  const handleRawDataCsvDownload = useCallback(
    async (tableKey: RawDataTableKey) => {
      setLoadingRawData(tableKey);
      try {
        const result = await revenueApi.getRawData(tableKey);
        if (result.rows.length === 0) {
          window.alert(t('revenue.rawDataEmpty', { table: tableKey }));
          return;
        }
        const csv = buildRawDataCsv(result.columns, result.rows);
        const today = new Date().toISOString().slice(0, 10);
        downloadCsv(`revenue_raw_${tableKey}_${today}.csv`, csv);
      } catch {
        window.alert(t('revenue.rawDataFetchError', { table: tableKey }));
      } finally {
        setLoadingRawData(null);
      }
    },
    [t],
  );

  const handleAllSqlDownload = useCallback(() => {
    const sql = buildSqlExportText();
    downloadSql('revenue_raw_queries.sql', sql);
  }, []);

  const handleIndividualSqlDownload = useCallback((queryKey: string) => {
    const query = RAW_DATA_QUERIES.find((q) => q.key === queryKey);
    if (query === undefined) return;
    const sql = buildIndividualSqlText(query);
    downloadSql(`revenue_sql_${queryKey}.sql`, sql);
  }, []);

  const handleLedgerCsvDownload = useCallback(() => {
    if (ledger === undefined) return;
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`revenue_ledger_${ledger.version}_${today}.csv`, buildLedgerCsv(ledger));
  }, [ledger]);

  const handleLogicDownload = useCallback(() => {
    const text = buildAggregationLogicText();
    downloadFile('revenue_aggregation_logic.txt', text, 'text/plain;charset=utf-8;');
  }, []);

  const noRecords = rows.length === 0;
  const csvMenuItems = useMemo(
    () => [
      ...(isOfficial
        ? [
            {
              label: t('revenue.csvLedger'),
              onClick: handleLedgerCsvDownload,
              // 最終更新は版全体から出るので、期間に値が無い（行が無い）ときも空の CSV を出さない
              disabled: ledger === undefined || ledger.rows.length === 0,
            },
          ]
        : [
            { label: t('revenue.csvDaily'), onClick: handleDailyCsvDownload, disabled: noRecords },
            {
              label: t('revenue.csvMonthly'),
              onClick: handleMonthlyCsvDownload,
              disabled: noRecords,
            },
          ]),
      ...RAW_DATA_TABLE_LABELS.map((table, i) => ({
        label: `raw: ${t(table.labelKey)}`,
        onClick: () => {
          void handleRawDataCsvDownload(table.key);
        },
        loading: loadingRawData === table.key,
        separator: i === 0,
      })),
    ],
    [
      handleDailyCsvDownload,
      handleLedgerCsvDownload,
      handleMonthlyCsvDownload,
      handleRawDataCsvDownload,
      isOfficial,
      ledger,
      loadingRawData,
      noRecords,
      t,
    ],
  );

  const sqlMenuItems = useMemo(
    () => [
      // 集計ロジック説明は旧Hub（v0）の計算の説明なので、v0 のときだけ出す
      ...(isOfficial ? [] : [{ label: t('revenue.sqlLogic'), onClick: handleLogicDownload }]),
      { label: t('revenue.sqlAll'), onClick: handleAllSqlDownload, separator: !isOfficial },
      ...RAW_DATA_QUERIES.map((q) => ({
        label: q.label,
        onClick: () => {
          handleIndividualSqlDownload(q.key);
        },
      })),
    ],
    [handleLogicDownload, handleAllSqlDownload, handleIndividualSqlDownload, isOfficial, t],
  );

  const monthOptions = useMemo(
    () => monthRange(REVENUE_START_DATE.slice(0, 7), jstDate(0).slice(0, 7)),
    [],
  );
  const snapshotLabel = isOfficial
    ? ledger?.run == null
      ? null
      : formatSnapshotJST(ledger.run.computedAt)
    : recordsQuery.data?.snapshotAt
      ? formatSnapshotJST(recordsQuery.data.snapshotAt)
      : null;

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        Finance
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('revenue.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{t('revenue.description')}</p>

      {!isOfficial && (
        <div
          role="status"
          className="mt-4 flex items-center gap-2 rounded-lg border border-[var(--color-warning)] bg-[var(--color-warning-muted)] px-4 py-2 text-sm text-[var(--color-text-primary)]"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('revenue.v0Banner')}
        </div>
      )}

      <Card className="mt-6 flex flex-wrap items-center gap-4 p-4">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
          <Select
            size="sm"
            label={t('revenue.versionLabel')}
            options={REVENUE_VERSION_OPTIONS.map((o) => ({
              value: o.version,
              label: t(o.labelKey),
            }))}
            value={version}
            aria-describedby={approvalId}
            onChange={(e) => {
              setVersion(RevenueLedgerVersionSchema.parse(e.target.value));
            }}
          />
          <div className="pb-1.5">
            <RevenueVersionApproval id={approvalId} version={version} />
          </div>
        </div>
        <DateRangeFilter
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          minDate={REVENUE_START_DATE}
        />
        {isOfficial && (
          <ChartFilterBar
            label={t('revenue.withdrawalLabel')}
            options={WITHDRAWAL_OPTIONS.map((o) => ({ key: o.key, label: t(o.labelKey) }))}
            value={includeWithdrawal ? 'include' : 'exclude'}
            onChange={(v) => {
              setIncludeWithdrawal(v === 'include');
            }}
          />
        )}
        <ViewModeTabs mode={viewMode} onChange={setViewMode} />
        <div className="flex items-center gap-2">
          <DropdownMenu trigger={{ label: 'CSV', icon: Download }} items={csvMenuItems} />
          <DropdownMenu trigger={{ label: 'SQL', icon: FileCode }} items={sqlMenuItems} />
        </div>
        {snapshotLabel !== null && (
          <span className="ml-auto text-xs text-[var(--color-text-muted)]">
            {t('common.lastUpdated')}: {snapshotLabel}
          </span>
        )}
      </Card>

      {isOfficial ? (
        <RevenueLedgerPanel
          viewMode={viewMode}
          from={from}
          toDate={toDate}
          fromMonth={fromMonth}
          toMonth={toMonth}
          monthOptions={monthOptions}
          includeWithdrawal={includeWithdrawal}
        />
      ) : viewMode === 'compare' ? (
        <RevenueCompareView monthOptions={monthOptions} defaultMonth={toMonth} />
      ) : viewMode === 'definition' ? (
        <>
          <p className="mt-6 text-sm text-[var(--color-text-secondary)]">
            {t('revenue.definitions.v0Note')}
          </p>
          <RevenueDefinitionView columns={columns} />
        </>
      ) : viewMode === 'validation' ? (
        validationQuery.isLoading ? (
          <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
        ) : validationQuery.isError ? (
          <Card className="mt-6 p-6">
            <p className="text-sm text-[var(--color-text-secondary)]">
              {t('revenue.validationFetchError')}
            </p>
          </Card>
        ) : (
          <RevenueValidationView entries={validationQuery.data?.entries ?? []} />
        )
      ) : recordsQuery.isLoading ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : recordsQuery.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('revenue.fetchError')}</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <LineChartIcon
            className="mx-auto h-8 w-8 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">{t('revenue.noData')}</p>
        </Card>
      ) : viewMode === 'chart' ? (
        <RevenueChartView rows={rows} />
      ) : viewMode === 'monthly' ? (
        <RevenueMonthlyView columns={columns} rows={rows} />
      ) : viewMode === 'deferred' ? (
        <RevenueDeferredView rows={rows} />
      ) : null}
    </div>
  );
}
